/**
 * Build an exportable COO Charter report out of the charter iframe's DOM.
 *
 * Brought across from `C:\Development\EOS` (`frontend/src/lib/charterReport.js`)
 * with the same logic and the same output; the only changes are the type
 * annotations this codebase's strict TypeScript needs.
 *
 * The charter is a self-contained HTML document; its notes and checkboxes carry
 * data-key attributes but the human-readable context (section heading, note
 * label, checklist item text) only exists in the markup. So we read structure
 * and values straight from the live document rather than trying to keep a
 * duplicate label table in sync with cooCharter.html.
 */

/** The standing context a note or checklist item sits inside. */
type Ctx = { name: string; blurb: string; bullets: string[] };
type Note = { label: string; prompt: string; value: string };
type Check = { label: string; done: boolean };
type Section = Ctx & { notes: Note[]; checks: Check[] };
/** A section as printed — one section can run across several slides. */
type Slide = Section & { cont: boolean };
type Stats = { notes: number; notesFilled: number; checks: number; checked: number };

export type CollectedCharter = { sections: Section[]; stats: Stats };

const text = (el: Element | null | undefined): string =>
  (el?.textContent || '').replace(/\s+/g, ' ').trim();

// Describe the block an element belongs to: its title, plus the standing
// context the charter prints around it — a priority's rationale and success
// criteria, a station's objectives, an opportunity's desired future state.
//
// That context is the question each note answers. Without it the export shows
// an answer under a generic label ("My notes & approach", "Answer", "Note")
// and the reader has to have the charter open beside it to know what was
// asked. The charter nests headings a few different ways, so try the most
// specific container first.
function contextFor(el: Element): Ctx {
  const station = el.closest('.stationcard');
  if (station) {
    const parts = [text(station.querySelector('.sc-head .day')), text(station.querySelector('.sc-head .verb'))];
    const name = parts.filter(Boolean).join(' · ');
    // A station's objectives sit in ul.ticks; ul.checks are its deliverables,
    // which are collected separately as checkboxes.
    if (name) return { name, blurb: '', bullets: bulletsIn(station, 'ul.ticks > li') };
  }

  const card = el.closest('.card, .prio, article');
  if (card) {
    const heading = card.querySelector('h3, h4');
    // Question cards carry no heading — their .tag is the title ("Service
    // delivery"), and without it all five collapse under the section's h2.
    const title = text(heading) || text(card.querySelector('.tag'));
    if (title) {
      const name = [text(card.querySelector('.num')), title].filter(Boolean).join(' · ');
      return { name, blurb: text(card.querySelector(':scope > p')), bullets: cardBullets(card) };
    }
  }

  // h1 included: the hero section (which holds the north-star note) titles
  // itself with one rather than the h2 every other section uses.
  const section = el.closest('section');
  const sectionHeading = section?.querySelector('.sec-head h2, h1, h2, h3');
  if (sectionHeading) {
    return { name: text(sectionHeading), blurb: text(section?.querySelector('.sec-head .lead')), bullets: [] };
  }
  return { name: '', blurb: '', bullets: [] };
}

const bulletsIn = (root: Element | null | undefined, selector: string): string[] =>
  [...(root?.querySelectorAll(selector) || [])].map(text).filter(Boolean);

// A card's criteria list, plus its "desired future state" panel if it has one.
// The panel's <b> label runs straight into the prose with no separator, so
// split it back apart rather than printing "Desired future stateMore …".
function cardBullets(card: Element): string[] {
  const bullets = bulletsIn(card, ':scope > ul.ticks > li');
  const fut = card.querySelector('.fut');
  if (fut) {
    const label = text(fut.querySelector('b'));
    const body = text(fut).slice(label.length).trim();
    bullets.push(body ? `${label} — ${body}` : label);
  }
  return bullets;
}

// Walk the charter in document order and collect filled notes + checklists,
// grouped under the section heading each belongs to.
export function collectCharter(cdoc: Document | null | undefined): CollectedCharter {
  if (!cdoc) return { sections: [], stats: { notes: 0, notesFilled: 0, checks: 0, checked: 0 } };

  const bySection = new Map<string, Section>();
  const section = (ctx: Ctx): Section => {
    const key = ctx.name || 'Charter';
    let found = bySection.get(key);
    if (!found) {
      found = { name: key, blurb: ctx.blurb, bullets: ctx.bullets, notes: [], checks: [] };
      bySection.set(key, found);
    }
    return found;
  };

  const stats: Stats = { notes: 0, notesFilled: 0, checks: 0, checked: 0 };

  // One combined pass so sections come out in the charter's own reading order
  // rather than all notes first and all checklists after.
  cdoc.querySelectorAll('textarea.note[data-key], .checks input[type=checkbox][data-key]').forEach((el) => {
    if (el.tagName === 'TEXTAREA') {
      const ta = el as HTMLTextAreaElement;
      stats.notes += 1;
      const value = (ta.value || '').trim();
      if (!value) return; // empty prompts are left out of the report
      stats.notesFilled += 1;
      section(contextFor(ta)).notes.push({
        label: text(ta.closest('.note-wrap')?.querySelector('.note-lab')) || 'Notes',
        prompt: ta.getAttribute('placeholder') || '',
        value,
      });
    } else {
      const box = el as HTMLInputElement;
      stats.checks += 1;
      if (box.checked) stats.checked += 1;
      section(contextFor(box)).checks.push({
        label: text(box.closest('label')) || box.dataset.key || '',
        done: box.checked,
      });
    }
  });

  // Drop sections that ended up with nothing worth printing.
  const sections = [...bySection.values()].filter((s) => s.notes.length || s.checks.length);
  return { sections, stats };
}

const ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};
const esc = (s: unknown): string => String(s ?? '').replace(/[&<>"']/g, (c) => ENTITIES[c] ?? c);

// Split a section across slides so nothing overflows: the first slide carries
// the section's own context (description, criteria, checklist) plus as many
// notes as still fit, and any remaining notes continue on follow-up slides.
// Most sections hold a single note and never split.
function slidesFor(section: Section): Slide[] {
  const out: Slide[] = [];
  const notes = [...section.notes];
  const context = section.checks.length + section.bullets.length;
  const first = notes.splice(0, context > 3 ? 1 : context ? 2 : 3);
  out.push({ ...section, notes: first, cont: false });
  while (notes.length) {
    out.push({ name: section.name, blurb: '', bullets: [], checks: [], notes: notes.splice(0, 3), cont: true });
  }
  return out;
}

type ReportOptions = { title?: string; savedAt?: string | null; savedBy?: string | null };

// A standalone slide deck. Presents in a browser (arrow keys / space), prints
// one slide per page, and carries its own editor: pictures can be added to any
// slide after export, then saved back out as a fresh self-contained file.
export function renderCharterReport(
  { sections, stats }: CollectedCharter,
  { title = 'COO Success Charter', savedAt, savedBy }: ReportOptions = {},
): string {
  const when = savedAt ? new Date(savedAt) : new Date();
  const stamp = isNaN(when.getTime()) ? '' : when.toLocaleDateString('en-US',
    { year: 'numeric', month: 'long', day: 'numeric' });

  const deck = sections.flatMap(slidesFor);

  const slideBody = (s: Slide) => `
      <div class="eyebrow">${esc(s.name)}${s.cont ? ' <span class="cont">continued</span>' : ''}</div>
      <div class="rule"></div>
      ${s.blurb ? `<p class="blurb">${esc(s.blurb)}</p>` : ''}
      ${s.bullets.length ? `<ul class="criteria">${s.bullets.map(b =>
        `<li>${esc(b)}</li>`).join('')}</ul>` : ''}
      ${s.checks.length ? `<ul class="checks">${s.checks.map(c =>
        `<li class="${c.done ? 'done' : ''}"><span class="box">${c.done ? '&#10003;' : ''}</span><span>${esc(c.label)}</span></li>`,
      ).join('')}</ul>` : ''}
      ${s.notes.map(n => `
      <div class="note">
        <div class="note-label">${esc(n.label)}</div>
        ${n.prompt ? `<div class="note-prompt">${esc(n.prompt)}</div>` : ''}
        <p>${esc(n.value).replace(/\n/g, '<br>')}</p>
      </div>`).join('')}`;

  const contentSlides = deck.map((s, i) => `
  <section class="slide">
    <div class="inner">
      ${slideBody(s)}
      <div class="shots"></div>
      <div class="editbar"><button class="addimg" type="button">+ Add picture</button>
        <span class="hint">or drag an image onto the slide / paste from the clipboard</span></div>
    </div>
    <div class="pagenum">${i + 2}</div>
  </section>`).join('');

  const empty = `
  <section class="slide"><div class="inner">
    <div class="eyebrow">Nothing captured yet</div><div class="rule"></div>
    <p class="empty">Add notes or tick deliverables in the charter, then export again.</p>
    <div class="shots"></div>
    <div class="editbar"><button class="addimg" type="button">+ Add picture</button></div>
  </div><div class="pagenum">2</div></section>`;

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(title)}</title>
<style>
  *{box-sizing:border-box}
  html,body{margin:0;padding:0;height:100%}
  body{font:16px/1.65 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#0f172a;
       background:#0b1f2c;overflow:hidden}
  .slide{display:none;height:100vh;width:100vw;padding:6vh 8vw;background:#fff;
         flex-direction:column;justify-content:center;position:relative}
  .slide.active{display:flex}
  .inner{max-width:60rem;width:100%;margin:0 auto;overflow-y:auto;max-height:82vh}

  .slide.title{background:#002f44;color:#fff;justify-content:center}
  .slide.title .kicker{color:#e88a7d;font-size:.8rem;font-weight:700;letter-spacing:.18em;
                       text-transform:uppercase;margin-bottom:1.2rem}
  .slide.title h1{font-size:clamp(2rem,5.5vw,3.6rem);line-height:1.1;margin:0 0 1.4rem;letter-spacing:-.02em}
  .slide.title .meta{color:#9db8cc;font-size:1rem}
  .stats{display:flex;gap:3.5rem;margin-top:3rem;flex-wrap:wrap}
  .stats b{display:block;font-size:2.2rem;color:#fff;line-height:1.1}
  .stats span{color:#9db8cc;font-size:.85rem;text-transform:uppercase;letter-spacing:.08em}

  .eyebrow{font-size:clamp(1.3rem,2.8vw,2rem);font-weight:800;color:#002f44;letter-spacing:-.01em}
  .cont{font-size:.75rem;font-weight:700;text-transform:uppercase;letter-spacing:.1em;
        color:#94a3b8;vertical-align:middle;margin-left:.5rem}
  .rule{width:60px;height:4px;background:#e04e3b;border-radius:2px;margin:.9rem 0 1.8rem}
  /* The charter's own words: what this section asks, and how it is judged. */
  .blurb{margin:-.9rem 0 1.3rem;font-size:1.12rem;line-height:1.55;color:#334155;max-width:48rem}
  ul.criteria{list-style:none;padding:0;margin:0 0 1.6rem;display:grid;gap:.35rem}
  ul.criteria li{position:relative;padding-left:1.15rem;color:#475569;font-size:1rem}
  ul.criteria li::before{content:"";position:absolute;left:0;top:.62em;width:6px;height:6px;
                         border-radius:50%;background:#4f82a6}
  ul.checks{list-style:none;padding:0;margin:0 0 1.6rem}
  ul.checks li{display:flex;align-items:flex-start;gap:.7rem;padding:.3rem 0;color:#64748b;font-size:1.05rem}
  ul.checks li.done{color:#0f172a}
  .box{flex:0 0 20px;width:20px;height:20px;border:2px solid #cbd5e1;border-radius:5px;display:inline-flex;
       align-items:center;justify-content:center;font-size:12px;color:#fff;margin-top:.25rem}
  li.done .box{background:#1b4b6b;border-color:#1b4b6b}
  .note{margin:0 0 1.5rem;padding-left:1.1rem;border-left:4px solid #e2e8f0}
  .note-label{font-weight:700;font-size:.72rem;text-transform:uppercase;letter-spacing:.09em;color:#1b4b6b}
  .note-prompt{font-size:.8rem;color:#a3b1c2;font-style:italic;margin-top:.15rem}
  .note p{margin:.5rem 0 0;white-space:pre-wrap;font-size:1.08rem;color:#1e293b}
  .empty{color:#94a3b8;font-style:italic}
  .pagenum{position:absolute;bottom:3vh;right:8vw;font-size:.75rem;color:#cbd5e1;font-variant-numeric:tabular-nums}

  /* Pictures added after export */
  .shots{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:1rem;margin-top:.5rem}
  .shots:empty{margin:0}
  .shot{position:relative;margin:0}
  /* Cap the height so a tall photo can't push the rest of the slide out of view. */
  .shot img{width:100%;max-height:42vh;object-fit:contain;background:#f8fafc;
            border-radius:10px;display:block;border:1px solid #e2e8f0}
  .shot .rm{position:absolute;top:.4rem;right:.4rem;width:26px;height:26px;border-radius:50%;border:0;
            background:rgba(15,23,42,.72);color:#fff;font-size:15px;line-height:1;cursor:pointer;display:none}
  body.editing .shot .rm{display:block}
  .editbar{display:none;margin-top:1.2rem;align-items:center;gap:.8rem;flex-wrap:wrap}
  body.editing .editbar{display:flex}
  .editbar .addimg{background:#e04e3b;color:#fff;border:0;border-radius:8px;padding:.5rem .9rem;
                   font-size:.85rem;font-weight:700;cursor:pointer}
  .editbar .hint{font-size:.78rem;color:#94a3b8}
  body.editing .slide.active{outline:3px dashed transparent;outline-offset:-14px}
  body.editing .slide.active.dragover{outline-color:#e04e3b;background:#fff8f7}

  .nav{position:fixed;bottom:2.2vh;left:50%;transform:translateX(-50%);display:flex;align-items:center;
       gap:.9rem;background:rgba(0,47,68,.9);color:#fff;padding:.45rem .9rem;border-radius:999px;
       font-size:.8rem;opacity:.3;transition:opacity .2s;z-index:10}
  .nav:hover,body.editing .nav{opacity:1}
  .nav button{background:none;border:0;color:#fff;font-size:1.1rem;cursor:pointer;padding:0 .3rem;line-height:1}
  .nav button:disabled{opacity:.3;cursor:default}
  .nav .txt{font-size:.78rem;font-weight:700;padding:.25rem .6rem;border-radius:999px}
  .nav .txt.on{background:#e04e3b}
  .counter{font-variant-numeric:tabular-nums;min-width:3.5rem;text-align:center}
  .sep{width:1px;height:16px;background:rgba(255,255,255,.25)}

  @media print{
    @page{size:landscape;margin:0}
    body{background:#fff;overflow:visible}
    .slide{display:flex !important;break-after:page;page-break-after:always;height:100vh}
    .slide:last-of-type{break-after:auto;page-break-after:auto}
    .inner{max-height:none;overflow:visible}
    .nav,.editbar,.shot .rm{display:none !important}
    .slide.title{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  }
</style></head>
<body>
  <section class="slide title active">
    <div class="inner">
      <div class="kicker">Operations</div>
      <h1>${esc(title)}</h1>
      <div class="meta">${esc(stamp)}${savedBy ? ` &middot; ${esc(savedBy)}` : ''}</div>
      <div class="stats">
        <div><b>${stats.notesFilled}/${stats.notes}</b><span>Prompts answered</span></div>
        <div><b>${stats.checked}/${stats.checks}</b><span>Deliverables complete</span></div>
        <div><b>${sections.length}</b><span>Sections</span></div>
      </div>
      <div class="shots"></div>
      <div class="editbar"><button class="addimg" type="button">+ Add picture</button></div>
    </div>
    <div class="pagenum" style="color:#4f7d96">1</div>
  </section>
  ${contentSlides || empty}

  <div class="nav">
    <button id="prev" aria-label="Previous slide">&#8249;</button>
    <span class="counter"><span id="cur">1</span> / <span id="total">1</span></span>
    <button id="next" aria-label="Next slide">&#8250;</button>
    <span class="sep"></span>
    <button class="txt" id="edit" type="button">Edit</button>
    <button class="txt" id="savecopy" type="button">Save copy</button>
  </div>
  <input type="file" id="picker" accept="image/*" multiple hidden>

<script>
(function(){
  var slides = Array.prototype.slice.call(document.querySelectorAll('.slide'));
  var i = 0, editing = false;
  var cur = document.getElementById('cur'),
      prev = document.getElementById('prev'), next = document.getElementById('next'),
      editBtn = document.getElementById('edit'), saveBtn = document.getElementById('savecopy'),
      picker = document.getElementById('picker');
  document.getElementById('total').textContent = slides.length;

  function show(n){
    i = Math.max(0, Math.min(slides.length - 1, n));
    slides.forEach(function(s, k){ s.classList.toggle('active', k === i); });
    cur.textContent = i + 1;
    prev.disabled = i === 0;
    next.disabled = i === slides.length - 1;
    var inner = slides[i] && slides[i].querySelector('.inner');
    if (inner) inner.scrollTop = 0;
  }
  prev.addEventListener('click', function(){ show(i - 1); });
  next.addEventListener('click', function(){ show(i + 1); });
  document.addEventListener('keydown', function(e){
    var typing = /^(INPUT|TEXTAREA)$/.test((e.target || {}).tagName || '');
    if (typing) return;
    if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') { show(i + 1); e.preventDefault(); }
    else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { show(i - 1); e.preventDefault(); }
    else if (e.key === 'Home') { show(0); }
    else if (e.key === 'End') { show(slides.length - 1); }
  });

  // --- pictures -----------------------------------------------------------
  // Large photos are downscaled before embedding: every image lives in this
  // file as a data URI, so full-resolution originals would bloat it into the
  // tens of megabytes and make the deck slow to open and impossible to email.
  var MAX_W = 1600;
  function toDataURL(file, cb){
    var reader = new FileReader();
    reader.onload = function(){
      var src = reader.result;
      // Small PNGs keep their exact pixels (screenshots of text stay crisp).
      if (file.type === 'image/png' && file.size < 1500000) return cb(src);
      var img = new Image();
      img.onload = function(){
        var w = img.width, h = img.height;
        if (w > MAX_W) { h = Math.round(h * MAX_W / w); w = MAX_W; }
        var c = document.createElement('canvas');
        c.width = w; c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        try { cb(c.toDataURL('image/jpeg', 0.85)); } catch (err) { cb(src); }
      };
      img.onerror = function(){ cb(src); };
      img.src = src;
    };
    reader.readAsDataURL(file);
  }
  function addPicture(slide, dataUrl){
    var shots = slide.querySelector('.shots');
    if (!shots) return;
    var fig = document.createElement('figure');
    fig.className = 'shot';
    var img = document.createElement('img');
    img.src = dataUrl;
    var rm = document.createElement('button');
    rm.className = 'rm'; rm.type = 'button'; rm.textContent = '\\u00d7';
    rm.title = 'Remove picture';
    fig.appendChild(img); fig.appendChild(rm);
    shots.appendChild(fig);
  }
  function handleFiles(slide, files){
    Array.prototype.slice.call(files)
      .filter(function(f){ return /^image\\//.test(f.type); })
      .forEach(function(f){ toDataURL(f, function(url){ addPicture(slide, url); }); });
  }
  // Removal is delegated, so it works for pictures added after load too.
  document.addEventListener('click', function(e){
    var t = e.target;
    if (t && t.classList && t.classList.contains('rm')) { t.parentNode.remove(); return; }
    if (t && t.classList && t.classList.contains('addimg')) {
      picker.dataset.slide = slides.indexOf(t.closest('.slide'));
      picker.value = '';
      picker.click();
    }
  });
  picker.addEventListener('change', function(){
    var slide = slides[Number(picker.dataset.slide) || 0];
    handleFiles(slide, picker.files);
  });
  // Drag an image file straight onto the slide.
  document.addEventListener('dragover', function(e){
    if (!editing) return;
    e.preventDefault();
    if (slides[i]) slides[i].classList.add('dragover');
  });
  document.addEventListener('dragleave', function(){ if (slides[i]) slides[i].classList.remove('dragover'); });
  document.addEventListener('drop', function(e){
    if (!editing) return;
    e.preventDefault();
    if (slides[i]) slides[i].classList.remove('dragover');
    if (e.dataTransfer && e.dataTransfer.files) handleFiles(slides[i], e.dataTransfer.files);
  });
  // Paste a screenshot from the clipboard.
  document.addEventListener('paste', function(e){
    if (!editing || !e.clipboardData) return;
    var items = Array.prototype.slice.call(e.clipboardData.items || []);
    var files = items.filter(function(it){ return it.kind === 'file'; })
                     .map(function(it){ return it.getAsFile(); })
                     .filter(Boolean);
    if (files.length) { e.preventDefault(); handleFiles(slides[i], files); }
  });

  editBtn.addEventListener('click', function(){
    editing = !editing;
    document.body.classList.toggle('editing', editing);
    editBtn.classList.toggle('on', editing);
    editBtn.textContent = editing ? 'Done' : 'Edit';
  });

  // Save the deck back out with the pictures baked in. The file is written
  // from the live DOM, so a saved copy reopens exactly as it looks now.
  saveBtn.addEventListener('click', function(){
    var wasEditing = editing;
    document.body.classList.remove('editing');
    slides.forEach(function(s, k){ s.classList.toggle('active', k === 0); });
    var html = '<!doctype html>\\n' + document.documentElement.outerHTML;
    document.body.classList.toggle('editing', wasEditing);
    show(i);
    var blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = (document.title || 'charter').replace(/[^a-z0-9]+/gi, '-').toLowerCase() + '.html';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(function(){ URL.revokeObjectURL(url); }, 1000);
  });

  window.addEventListener('beforeprint', function(){ slides.forEach(function(s){ s.classList.add('active'); }); });
  window.addEventListener('afterprint', function(){ show(i); });
  show(0);
})();
<\/script>
</body></html>`;
}

